import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, ArchiveRestore, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const euro = (n) => (Number(n) || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const RigaMancante = ({ r, checked, onToggle }) => (
  <label className="flex items-center gap-3 px-3 py-2.5 border rounded-xl cursor-pointer hover:bg-amber-50/60 transition-colors" data-testid={`vecchia-carta-riga-${r.chiave.replace(/\s+/g, '-')}`}>
    <input type="checkbox" className="h-4 w-4 accent-amber-700" checked={checked} onChange={onToggle} data-testid={`vecchia-carta-check-${r.chiave.replace(/\s+/g, '-')}`} />
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-semibold text-gray-900 truncate">{r.nome}</span>
      <span className="block text-xs text-gray-500 truncate">{[r.cantina, r.tipo_vino, r.regione].filter(Boolean).join(' · ')}</span>
    </span>
    <span className="text-sm font-semibold text-gray-800 tabular-nums">{r.prezzo ? euro(r.prezzo) : '—'}</span>
  </label>
);

export const VecchiaCartaDialog = ({ open, onClose, getAuthHeaders, onAggiunti }) => {
  const [dati, setDati] = useState(null);
  const [caricamento, setCaricamento] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [mostraPresenti, setMostraPresenti] = useState(false);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      const r = await axios.get(`${API}/api/vini-vecchia-carta`, { headers: getAuthHeaders() });
      setDati(r.data);
      setSel(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossibile leggere la vecchia carta');
    } finally {
      setCaricamento(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { if (open) carica(); }, [open, carica]);

  if (!open) return null;
  const mancanti = dati?.mancanti || [];
  const presenti = dati?.presenti || [];
  const toggle = (k) => setSel(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const tutti = mancanti.length > 0 && sel.size === mancanti.length;

  const aggiungi = async () => {
    setSalvo(true);
    try {
      const r = await axios.post(`${API}/api/vini-vecchia-carta/aggiungi`, { chiavi: [...sel] }, { headers: getAuthHeaders() });
      const n = r.data.aggiunti.length;
      if (n) toast.success(n === 1 ? `${r.data.aggiunti[0]} aggiunto alla carta` : `${n} vini aggiunti alla carta`);
      if (r.data.saltati.length) toast.warning(`Non aggiunti: ${r.data.saltati.join(', ')}`);
      if (n) await onAggiunti?.(r.data.ids || []);
      await carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante l\'aggiunta');
    } finally {
      setSalvo(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="vecchia-carta-dialog">
      <div className="bg-white w-full sm:max-w-3xl max-h-[95vh] sm:rounded-2xl rounded-t-2xl flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div>
            <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 text-gray-900"><ArchiveRestore className="h-5 w-5 text-amber-700" /> Vini dalla vecchia carta</h2>
            <p className="text-xs text-gray-500 mt-0.5">Vini della carta PDF caricata in passato che non risultano in nessuna fattura. Spunta quelli che vendi ancora: restano in carta in modo permanente, con il prezzo della vecchia carta.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="vecchia-carta-chiudi"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="vecchia-carta-lista">
          {caricamento && <p className="py-10 text-center text-sm text-gray-500 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Confronto la vecchia carta con la Lista Vini…</p>}
          {!caricamento && dati && dati.totale === 0 && <p className="py-10 text-center text-sm text-gray-500" data-testid="vecchia-carta-vuota">Nessuna vecchia carta dei vini trovata.</p>}
          {!caricamento && dati && dati.totale > 0 && (
            <>
              <div className="flex items-center justify-between text-xs text-gray-600 pb-1">
                <span data-testid="vecchia-carta-conteggio"><b className="text-gray-900">{mancanti.length}</b> da recuperare · {presenti.length} già in carta (su {dati.totale})</span>
                {mancanti.length > 0 && (
                  <button onClick={() => setSel(tutti ? new Set() : new Set(mancanti.map(m => m.chiave)))} className="text-amber-800 font-medium hover:underline" data-testid="vecchia-carta-seleziona-tutti">
                    {tutti ? 'Deseleziona tutti' : 'Seleziona tutti'}
                  </button>
                )}
              </div>
              {!mancanti.length && <p className="py-6 text-center text-sm text-emerald-700 flex items-center justify-center gap-2" data-testid="vecchia-carta-tutto-ok"><CheckCircle2 className="h-5 w-5" /> Tutti i vini della vecchia carta sono già in Lista Vini.</p>}
              {mancanti.map(r => <RigaMancante key={r.chiave} r={r} checked={sel.has(r.chiave)} onToggle={() => toggle(r.chiave)} />)}
              {presenti.length > 0 && (
                <div className="pt-3">
                  <button onClick={() => setMostraPresenti(v => !v)} className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900" data-testid="vecchia-carta-toggle-presenti">
                    {mostraPresenti ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} Già in carta ({presenti.length})
                  </button>
                  {mostraPresenti && (
                    <ul className="mt-2 divide-y border rounded-xl text-xs" data-testid="vecchia-carta-presenti">
                      {presenti.map(r => (
                        <li key={r.chiave} className="px-3 py-2 flex flex-wrap gap-x-2">
                          <span className="text-gray-500">{r.nome_vecchia_carta}</span>
                          <span className="text-gray-400">→</span>
                          <span className="text-gray-900 font-medium">{r.in_carta_come}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100" data-testid="vecchia-carta-annulla">Chiudi</button>
          <button onClick={aggiungi} disabled={!sel.size || salvo}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-700 text-white text-sm font-medium hover:bg-amber-800 disabled:opacity-50"
            data-testid="vecchia-carta-aggiungi">
            {salvo ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArchiveRestore className="h-4 w-4" />}
            Aggiungi {sel.size || ''} {sel.size === 1 ? 'vino' : 'vini'} alla carta
          </button>
        </div>
      </div>
    </div>
  );
};
